/**
 * A collapsible panel on the clinician side.
 *
 * The prescription builder grew three of these — how the program runs, safety
 * events, safety settings — each above the exercise list and each pushing it
 * further down the page. They collapse, with the same chevron the exercise
 * cards use, because they are the same kind of thing: a block you open when you
 * are working on it and leave shut when you are not.
 *
 * ONE COMPONENT RATHER THAN THREE COPIES. The chevron's size was set
 * deliberately (see `.disclosure` — a chevron glyph renders small inside its em
 * box and needs a larger nominal size to read as an equal part of a header),
 * and three copies of that reasoning would eventually become three sizes.
 *
 * COLLAPSED IS THE RESTING STATE, matching the exercise cards. Every panel
 * takes a `summary` for that reason: shut, it still has to say enough that the
 * doctor knows whether to open it. A panel that collapses to nothing but its
 * own title is a panel he has to open every time to find out if it matters.
 */

import { useId, useState, type ReactNode } from 'react';
import styles from './Panel.module.css';

export interface PanelProps {
  title: string;
  /** One line, read when the panel is shut. Say the state, not the purpose. */
  summary: string;
  /** Shown beside the title in both states — a save state, a standing flag. */
  aside?: ReactNode;
  /**
   * Open on first render. Used for something that should not wait to be found:
   * a safety event nobody has looked at yet.
   */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Panel({ title, summary, aside, defaultOpen = false, children }: PanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <section className={styles.panel} aria-label={title}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen(!open)}
        >
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
          <span className={styles.visuallyHidden}>
            {open ? 'Collapse' : 'Expand'} {title}
          </span>
        </button>

        <div className={styles.identity}>
          <h2 className={styles.title}>{title}</h2>
          {!open && <p className={styles.summary}>{summary}</p>}
        </div>

        {aside && <div className={styles.aside}>{aside}</div>}
      </header>

      {/*
        Unmounted when shut rather than hidden. These panels hold live forms and
        a running query; leaving a collapsed one mounted means tab order and
        screen readers reach controls that are not on screen.
      */}
      {open && (
        <div id={bodyId} className={styles.body}>
          {children}
        </div>
      )}
    </section>
  );
}
